/**
 * UserAvatar — Profile picture with presence indicator
 *
 * Displays a user's avatar image with a coloured dot (bottom-right)
 * indicating presence status:
 *   • green  = online (active)
 *   • orange = away   (tab hidden or idle for 5+ minutes)
 *   • hidden = offline
 *
 * Usage:
 *   <UserAvatar userGuid={user.id} avatarUrl={user.avatar} userName={user.name} size="md" />
 */

import React, { useMemo, useState } from 'react';
import { usePresence } from '../../presence/usePresence';
import { mediaSrc } from '../../api/mediaUrl'; // tolerant image-src resolver
import styles from './UserAvatar.module.css';

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

interface UserAvatarProps {
  userGuid: string;
  avatarUrl?: string;
  userName?: string;
  size?: AvatarSize;
  showPresence?: boolean;
  interactive?: boolean;
  onClick?: () => void;
  className?: string;
}

const sizeMap: Record<AvatarSize, number> = {
  xs: 28,
  sm: 32,
  md: 40,
  lg: 56,
  xl: 80,
};

const dotSizeMap: Record<AvatarSize, number> = {
  xs: 10,
  sm: 16,
  md: 18,
  lg: 24,
  xl: 32,
};

export default function UserAvatar({
  userGuid,
  avatarUrl,
  userName = '?',
  size = 'md',
  showPresence = true,
  interactive = false,
  onClick,
  className,
}: UserAvatarProps) {
  const { userStatus } = usePresence(userGuid);
  const [failedUrl, setFailedUrl] = useState<string | null>(null);

  const avatarSize = sizeMap[size];
  const dotSize = dotSizeMap[size];

  // Normalise whatever was passed (bare key, FileStorage path, external URL)
  // into a renderable same-origin src. Idempotent, so pre-resolved URLs are safe.
  const resolvedAvatarUrl = mediaSrc(avatarUrl);

  // Map presence status → CSS class
  const presenceDotClass = useMemo(() => {
    switch (userStatus) {
      case 'online': return styles.online;
      case 'idle':   return styles.away;   // 'idle' in store → 'away' visually
      default:       return styles.offline;
    }
  }, [userStatus]);

  // Generate initials as fallback
  const initials = useMemo(() => {
    if (!userName) return '?';
    return userName
      .split(' ')
      .slice(0, 2)
      .map((n) => n[0]?.toUpperCase() || '')
      .join('');
  }, [userName]);

  const containerStyle: React.CSSProperties = {
    width: avatarSize,
    height: avatarSize,
  };

  const dotStyle: React.CSSProperties = {
    width: dotSize,
    height: dotSize,
  };

  return (
    <div
      className={`${styles.avatarContainer} ${interactive ? styles.interactive : ''} ${className || ''}`}
      style={containerStyle}
      onClick={onClick}
      title={userName}
    >
      <div className={styles.avatarImage}>
        {resolvedAvatarUrl && resolvedAvatarUrl !== failedUrl ? (
          <img src={resolvedAvatarUrl} alt={userName} onError={() => setFailedUrl(resolvedAvatarUrl)} />
        ) : (
          <div className={styles.initials}>{initials}</div>
        )}
      </div>

      {showPresence && (
        <div
          className={`${styles.presenceDot} ${presenceDotClass}`}
          style={dotStyle}
        />
      )}
    </div>
  );
}
